import React, { useEffect, useMemo, useState } from "react";
import { useAuthStore } from "../store/authStore";

function getPersistedToken() {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.token || null;
  } catch {
    return null;
  }
}

export default function BotStatePage() {
  const { token: storeToken, isAuthenticated } = useAuthStore();

  const token = useMemo(() => {
    return storeToken || getPersistedToken() || null;
  }, [storeToken]);

  const [botStatus, setBotStatus] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;

    let mounted = true;

    const fetchStatus = async () => {
      try {
        setError("");
        const res = await fetch("/api/bot/status", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        if (mounted) setBotStatus(data);
      } catch (e) {
        if (mounted) setError("Bot status failed to load (403 = token/header not reaching backend).");
      }
    };

    fetchStatus();
    const t = setInterval(fetchStatus, 5000);

    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [token]);

  const running = !!botStatus?.running || !!botStatus?.is_running;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-black text-text-main">Bot State</h1>
        <p className="text-sm text-text-muted">Heartbeat + runtime status (per user)</p>
      </div>

      {/* Always-visible debug (remove later) */}
      <div className="border border-border/40 rounded-sm p-3 bg-black/30 text-xs text-text-muted">
        <div>isAuthenticated: {String(isAuthenticated)}</div>
        <div>storeToken present: {String(!!storeToken)}</div>
        <div>token present: {String(!!token)}</div>
      </div>

      {!token && (
        <div className="border border-border/40 rounded-sm p-4 bg-surface text-sm text-text-main">
          No token found yet. If you’re logged in, refresh once. If not, log in first.
        </div>
      )}

      {error && (
        <div className="border border-destructive/40 rounded-sm p-4 bg-black/30 text-destructive text-sm">
          {error}
        </div>
      )}

      <div className="border border-border/40 rounded-sm p-4 bg-surface">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-text-main">Heartbeat</h3>
          <span className={`text-xs font-bold ${running ? "text-green-400" : "text-red-400"}`}>
            {running ? "RUNNING" : "STOPPED"}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-text-muted">Last Tick</div>
            <div className="text-text-main font-medium">{botStatus?.last_tick_at || "—"}</div>
          </div>
          <div>
            <div className="text-text-muted">Tick Message</div>
            <div className="text-text-main font-medium">{botStatus?.last_tick_msg || "—"}</div>
          </div>
          <div>
            <div className="text-text-muted">Active Pairs</div>
            <div className="text-text-main font-medium">{botStatus?.active_pairs ?? "—"}</div>
          </div>
          <div>
            <div className="text-text-muted">Last Pair Processed</div>
            <div className="text-text-main font-medium">{botStatus?.last_pair_processed || "—"}</div>
          </div>
          <div>
            <div className="text-text-muted">Last Signal</div>
            <div className="text-text-main font-medium">{botStatus?.last_signal || "—"}</div>
          </div>
          <div>
            <div className="text-text-muted">Last Updated</div>
            <div className="text-text-main font-medium">{botStatus?.last_updated || "—"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
